import { useState, useRef } from "react";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import cross from "../../assets/cross.svg";
import { descriptFormat, mailFormat } from "../../utils/regex";

import firebase from "../../firebase/firebase.utils";
import Upload from "../../assets/upload-icon.svg";
import ButtonPrimary from "../button-primary/button-primary.component";
import InputField from "../input-field/input-field.component";
import Delete from "../../assets/upload-delete.svg";
import File from "../../assets/file-icon.svg";
import "./floating-help.styles.scss";
import { formatFileName } from "../../utils/fileTypesUtil";
import {
  convertFileToBase64,
  getFileExtensionFromFile,
} from "../../utils/fileUtilities";

let initialState = {
  email: "",
  description: "",
};

const FloatingHelp = ({ showHelp, setShowHelp }) => {
  const [error, setError] = useState({
    email: false,
    description: false,
    file: false,
    submit: false,
  });
  const [loading, setLoading] = useState(false);
  const [issueData, setIssueData] = useState(initialState);
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);
  const currentState = useSelector((state) => state);
  const user = useSelector((state) => state.base.user);

  useEffect(() => {
    if (user) {
      setIssueData({
        email: user.email,
        description: "",
        name: user.displayName,
      });
    }
  }, [user]);

  const handleChange = (value, name) => {
    let tempError = { ...error };

    // Clear submit error when user makes changes
    tempError.submit = false;

    if (value !== "") {
      if (name === "email") {
        if (value.match(mailFormat)) {
          tempError = { ...tempError, [name]: false };
        } else {
          tempError = { ...tempError, [name]: true };
        }
      }
      if (name === "description") {
        if (value.match(descriptFormat)) {
          tempError = { ...tempError, [name]: false };
        } else {
          tempError = { ...tempError, [name]: true };
        }
      }
    } else {
      tempError = { ...tempError, [name]: true };
    }

    setError({ ...tempError });
    setIssueData({ ...issueData, [name]: value });
  };

  const handleFileClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.size <= 5 * 1024 * 1024) {
      setFile(selectedFile);
      setError({ ...error, file: false, submit: false });
    } else {
      setFile(null);
      setError({ ...error, file: "File size exceeds 5 MB", submit: false });
    }
  };

  const handleDeleteFile = () => {
    setFile(null);
    setError({ ...error, file: false, submit: false });
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      let image = null;

      // Try to capture screenshot using dynamic import for better compatibility
      try {
        console.log("Starting screenshot capture process...");

        // Dynamic import to handle potential module loading issues
        const html2canvasModule = await import("html2canvas");
        const html2canvasFunc = html2canvasModule.default || html2canvasModule;

        console.log("html2canvas loaded successfully");
        console.log("html2canvas function type:", typeof html2canvasFunc);

        // Get the root element or body
        const targetElement = document.getElementById("root") || document.body;
        console.log("Target element for screenshot:", targetElement.tagName);
        console.log("Full page dimensions:", {
          scrollHeight: targetElement.scrollHeight,
          scrollWidth: targetElement.scrollWidth,
          clientHeight: targetElement.clientHeight,
          clientWidth: targetElement.clientWidth,
        });

        // Calculate appropriate scale based on page size to avoid memory issues
        const pageSize = targetElement.scrollHeight * targetElement.scrollWidth;
        const maxPixels = 3000000; // 3 million pixels max to avoid memory issues
        let scale = 0.5;

        if (pageSize > maxPixels) {
          scale = Math.sqrt(maxPixels / pageSize);
          console.log(
            "🔧 Large page detected! Reducing scale to avoid memory issues"
          );
          console.log("📏 Page size:", pageSize, "pixels");
          console.log("📉 Adjusted scale:", scale.toFixed(3));
        } else {
          console.log(
            "📏 Page size:",
            pageSize,
            "pixels - using standard scale"
          );
        }

        console.log("🎯 Starting capture with scale:", scale);
        console.log("⏱️ This may take a few seconds for large pages...");

        // Add timeout to prevent hanging
        const captureWithTimeout = (element, options, timeoutMs = 15000) => {
          return Promise.race([
            html2canvasFunc(element, options),
            new Promise((_, reject) =>
              setTimeout(
                () =>
                  reject(new Error(`Screenshot timeout after ${timeoutMs}ms`)),
                timeoutMs
              )
            ),
          ]);
        };

        const canvas = await captureWithTimeout(targetElement, {
          height: targetElement.scrollHeight, // Full page height
          width: targetElement.scrollWidth, // Full page width
          scale: scale, // Dynamic scale based on page size
          useCORS: true,
          allowTaint: false,
          backgroundColor: "#ffffff",
          logging: false,
          scrollX: 0,
          scrollY: 0,
          x: 0, // Start from top-left
          y: 0, // Start from top-left
          ignoreElements: (element) => {
            // Skip the help modal itself to avoid recursion
            if (element.classList?.contains("help-modal")) {
              console.log("Skipping help modal element");
              return true;
            }
            // Skip problematic elements
            if (
              element.tagName === "IFRAME" ||
              element.tagName === "SCRIPT" ||
              element.tagName === "NOSCRIPT"
            ) {
              return true;
            }
            return false;
          },
        });

        console.log("🖼️ Canvas result:", {
          canvas: !!canvas,
          hasToDataURL: !!(canvas && canvas.toDataURL),
          canvasType: typeof canvas,
          canvasWidth: canvas?.width,
          canvasHeight: canvas?.height,
        });

        if (canvas && canvas.toDataURL) {
          console.log("✅ Converting canvas to image...");
          image = canvas.toDataURL("image/png", 0.7);
          console.log("🎉 Screenshot captured successfully!");
          console.log(
            "📐 Canvas dimensions:",
            canvas.width,
            "x",
            canvas.height
          );
          console.log("📊 Image data URL length:", image.length, "characters");
          console.log(
            "💾 Estimated image size:",
            Math.round((image.length * 0.75) / 1024),
            "KB"
          );
        } else {
          console.log("❌ Canvas creation failed or invalid canvas");
          console.log("🔄 Canvas object:", canvas);

          // Try alternative approach with document.body
          console.log(
            "🔄 Trying alternative full-page capture with document.body..."
          );

          // Calculate scale for body element too
          const bodyPageSize =
            document.body.scrollHeight * document.body.scrollWidth;
          let bodyScale = 0.4;
          if (bodyPageSize > maxPixels) {
            bodyScale = Math.sqrt(maxPixels / bodyPageSize);
            console.log(
              "🔧 Adjusting body capture scale to:",
              bodyScale.toFixed(3)
            );
          }

          const bodyCanvas = await captureWithTimeout(document.body, {
            height: document.body.scrollHeight,
            width: document.body.scrollWidth,
            scale: bodyScale,
            useCORS: true,
            allowTaint: false,
            backgroundColor: "#ffffff",
            logging: false,
            scrollX: 0,
            scrollY: 0,
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
            ignoreElements: (element) => {
              if (element.classList?.contains("help-modal")) {
                return true;
              }
              return false;
            },
          });

          if (bodyCanvas && bodyCanvas.toDataURL) {
            image = bodyCanvas.toDataURL("image/png", 0.7);
            console.log("✅ Alternative full-page screenshot captured!");
            console.log(
              "📐 Canvas dimensions:",
              bodyCanvas.width,
              "x",
              bodyCanvas.height
            );
          } else {
            image = null;
          }
        }
      } catch (screenshotError) {
        console.error("Screenshot capture failed:", screenshotError);
        console.error("Error details:", screenshotError.message);

        // Try simple fallback without html2canvas
        try {
          console.log("Trying basic canvas fallback...");
          // Create a simple colored canvas as a placeholder with full page dimensions
          const fallbackCanvas = document.createElement("canvas");
          const bodyHeight = document.body.scrollHeight || 600;
          const bodyWidth = document.body.scrollWidth || 800;
          fallbackCanvas.width = Math.min(bodyWidth, 1600);
          fallbackCanvas.height = Math.min(bodyHeight, 1200);
          const ctx = fallbackCanvas.getContext("2d");
          ctx.fillStyle = "#f8f9fa";
          ctx.fillRect(0, 0, fallbackCanvas.width, fallbackCanvas.height);
          ctx.fillStyle = "#333";
          ctx.font = "24px Arial";
          ctx.fillText("Full Page Screenshot Unavailable", 50, 100);
          ctx.font = "18px Arial";
          ctx.fillText(
            `Attempted dimensions: ${bodyWidth} x ${bodyHeight}`,
            50,
            140
          );
          ctx.fillText("Please attach screenshots manually", 50, 180);
          console.log(
            "Fallback canvas created with dimensions:",
            fallbackCanvas.width,
            "x",
            fallbackCanvas.height
          );

          image = fallbackCanvas.toDataURL("image/png");
          console.log("✅ Fallback placeholder created");
        } catch (fallbackError) {
          console.error("Even fallback failed:", fallbackError);
          image = null;
        }
      }

      console.log("file", file);

      let fileBase64 = null;
      let extension = null;

      if (file) {
        fileBase64 = await convertFileToBase64(file);
        extension = getFileExtensionFromFile(file);
      }

      console.log("image", image);

      const reportIssue = firebase.functions().httpsCallable("reportIssue");
      const dataToSend = {
        ...issueData,
        image: image, // Will be null if screenshot failed
        state: JSON.stringify(currentState),
        Uploadfile: fileBase64,
        extension,
      };

      console.log("dataToSend", dataToSend);
      const resp = await reportIssue(dataToSend);

      if (resp) {
        // Reset form to initial state
        setError({ email: false, description: false, file: false });
        setIssueData({
          email: user?.email || "",
          description: "",
          name: user?.displayName || "",
        });
        setFile(null);
        setShowHelp(false);

        // Show success message or notification here if needed
        console.log("Issue reported successfully");
      }
    } catch (err) {
      console.error("Error in handleSubmit:", err);

      // Set error state to show user-friendly message
      setError((prev) => ({
        ...prev,
        submit: "Failed to submit the issue. Please try again.",
      }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="button-floating-container">
      {showHelp ? (
        <div className="help-modal">
          <div className="help-modal-header">
            <div className="help-modal-header-text">Report an issue</div>
            <div className="help-modal-header-close">
              <img
                className="cross-img"
                src={cross}
                alt="Close"
                onClick={() => {
                  setShowHelp(false);
                }}
              />
            </div>
          </div>
          <div className="help-modal-body">
            <div className="email-section">
              <span>From :</span>
              <InputField
                error={error.email}
                success={!error.email && issueData.email !== ""}
                value={issueData.email}
                onChange={(e) => handleChange(e.target.value, "email")}
                placeholder="Your email address"
                disabled={loading}
              />
            </div>

            <InputField
              error={error.description}
              success={!error.description && issueData.description !== ""}
              value={issueData.description}
              onChange={(e) => handleChange(e.target.value, "description")}
              textarea
              disabled={loading}
              placeholder="Describe your issue"
            />

            <div className="upload-section">
              {!file ? (
                <button className="upload-file-btn" onClick={handleFileClick}>
                  <div className="upload-btn-inner">
                    <img src={Upload} alt="Upload icon" />
                    <span>Upload file</span>
                  </div>
                </button>
              ) : (
                <div className="file-details">
                  <div className="file-name-section">
                    <img src={File} alt="file icon" />
                    <span className="file-name">
                      {formatFileName(file.name)}
                    </span>
                  </div>
                  <img
                    src={Delete}
                    alt="Delete icon"
                    onClick={handleDeleteFile}
                  />
                </div>
              )}
              <input
                type="file"
                accept="image/*,.pdf,.txt,.doc,.docx,.log"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: "none" }}
              />

              <span className="size-text">Maximum upload size: 5 MB</span>
              {error.file && <span className="error-text">{error.file}</span>}
            </div>

            {error.submit && (
              <div className="submit-error">
                <span className="error-text">{error.submit}</span>
              </div>
            )}

            <ButtonPrimary
              disabled={
                error.email ||
                error.description ||
                loading ||
                issueData.email === "" ||
                issueData.description === "" ||
                error.file
              }
              center
              mid
              loading={loading}
              onClick={handleSubmit}
            >
              Send
            </ButtonPrimary>
          </div>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};

export default FloatingHelp;
